import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { SortingState } from "@tanstack/react-table";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import StatusBadge from "Common/Components/StatusBadge/StatusBadge";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import LoadingSpinner from "components/LoadingSpinner";
import Colors, { MecordColors } from "Constants/Color";
import { app_modules } from "helpers/apps_modules_list";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import usePermission from "hooks/usePermission";
import { showCustomSwal, showLoading, hideLoading } from "helpers/swalHelper";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { createSelector } from "reselect";
import {
  exportApprovePurchaseRequestCSV,
  fetchApprovedPurchaseRequest,
  revertApprovePr,
} from "slices/Procurement/purchase-order/approved-pr/thunk";
import Swal from "sweetalert2";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import StatusChip from "pages/Enquiry/Components/StatusChip";
import Chip from "components/Chips/Chip";
import { hideTagsSearchListingSection } from "../../../utils/tagsSearchEmpty";

type ApprovedPRProps = {
  handleTabChange?: (tabIndex: number) => void;
  onTableReady?: (table: any) => void;
  noWrapper?: boolean;
  /** When true, empty tag-search tab still renders (table shows NoDataFound) if sibling tab has data */
  forceShowOnTagsSearch?: boolean;
};
const ApprovedPR: React.FC<ApprovedPRProps> = ({
  noWrapper = false,
  forceShowOnTagsSearch = false,
}) => {
  document.title = "Approved Purchase Request List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { tags: tagsParam } = useParams<{ tags?: string }>();

  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.purchase_management?.app_name,
    app_modules?.purchase_management?.modules?.purchase_order
  );

  const selectAP = createSelector(
    (state: any) => state.AReducer,
    (pr) => ({
      list: pr.list,
      rowCount: pr.rowCount,
      loading: pr.loading,
      error: pr.error,
    })
  );
  const { list, rowCount, loading, error } = useSelector(selectAP);

  const tableState = usePreservedTableState({
    key: TableKeys.PURCHASE_MANAGEMENT.APRPOVED_PR,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  const [tableInstance, setTableInstance] = useState<any>();
  // const [search, setSearch] = useState("");
  // const [searchInput, setSearchInput] = useState("");
  const [revertingId, setRevertingId] = useState<string | null>(null);

  // Memoize sort values to prevent unnecessary re-renders
  const sortField = useMemo(() => sorting[0]?.id ?? "", [sorting]);
  const sortDirection = useMemo(
    () => (sorting[0]?.desc ? "desc" : "asc"),
    [sorting]
  );
  const tagFromUrl = useMemo(() => {
    if (!tagsParam) return "";
    try {
      return decodeURIComponent(tagsParam).trim();
    } catch {
      return tagsParam.trim();
    }
  }, [tagsParam]);
  const isTagSearch = Boolean(tagFromUrl);
  const effectiveSearch = tagFromUrl || search;

  // Memoize filterValue string to prevent unnecessary re-renders
  const filterValueString = useMemo(
    () => JSON.stringify(filterValue),
    [filterValue]
  );

  // Fetch data when dependencies actually change
  useEffect(() => {
    dispatch(
      fetchApprovedPurchaseRequest({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search: effectiveSearch,
        ...(isTagSearch ? { type: "tag", search_type: "tag" } : {}),
        filters: filterValue,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    effectiveSearch,
    filterValueString,
    isTagSearch,
  ]);

  const handleRevert = (pr_id: string) => {
    showCustomSwal({
      title: "Are you sure?",
      text: "You want to revert this Approved PR!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: MecordColors.ThemeBGColor,
      cancelButtonColor: "#f46a6a",
      confirmButtonText: "Yes, revert it!",
      cancelButtonText: "Cancel",
    }).then((result: any) => {
      if (result.isConfirmed) {
        // show loading modal and mark which PR is reverting
        setRevertingId(pr_id);
        showLoading("Reverting...");
        dispatch(revertApprovePr(pr_id))
          .unwrap()
          .then((res: any) => {
            hideLoading();
            setRevertingId(null);
            if (res) {
              showCustomSwal({
                title: "Reverted!",
                text: "Approved PR has been reverted.",
                icon: "success",
              });
              dispatch(
                fetchApprovedPurchaseRequest({
                  page: pageIndex + 1,
                  pageSize,
                  sort: sortField,
                  order: sortField ? sortDirection : "",
                  search,
                  filters: filterValue,
                })
              );
            }
          })
          .catch((error: any) => {
            hideLoading();
            setRevertingId(null);
            console.error("Failed to revert Approved PR", error);
            showCustomSwal({
              title: "Error",
              text: "Failed to revert Approved PR",
              icon: "error",
            });
          });
      }
    });
  };
  // Debouncing
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    if (!tagFromUrl) return;
    setSearchInput(tagFromUrl);
    setSearch(tagFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagFromUrl]);

  useEffect(() => {
    setData(list);
  }, [list]);

  const columns = useMemo(
    () => [
      // {
      //   header: "Sr No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      // },
      {
        header: "PR No.",
        accessorKey: "pr_no",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_REQUEST?.FILTER_DROPDOWN_APPROVED_PR(
          "pr_no"
        )}`,
        cell: ({ row }: { row: any }) => {
          return row.original.pr_no || "-";
        },
      },

      {
        header: "PR Date",
        accessorKey: "pr_date",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.pr_date} />
        ),
      },
      {
        header: "Initiated By",
        accessorKey: "initiated_by",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_REQUEST?.FILTER_DROPDOWN_APPROVED_PR(
          "initiated_by"
        )}`,
        cell: ({ row }: { row: any }) => {
          return row.original.initiated_by || "-";
        },
      },
      {
        header: "PR Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_REQUEST?.FILTER_DROPDOWN_APPROVED_PR(
          "status"
        )}`,
        cell: ({ row }: { row: any }) => {
          const status: string = row.original.status;
          return <StatusChip status={row.original?.status} />;

          // let backgroundColor = "#ddd";
          // if (status?.toLowerCase() === "pending") {
          //   backgroundColor = "#FDE4B4";
          // }
          // if (status?.toLowerCase() === "approved") {
          //   backgroundColor = "#B7FAEB";
          // }

          // return (
          //   <StatusBadge
          //     status={status}
          //     bgColor={backgroundColor}
          //     color="#070707"
          //   />
          // );
        },
      },
      {
        header: "Reviewed By",
        accessorKey: "approved_by_user__username",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_REQUEST?.FILTER_DROPDOWN_APPROVED_PR(
          "approved_by_user__username"
        )}`,
        cell: ({ row }: { row: any }) => {
          const u = row.original?.approved_by_user;
          if (!u) return "-";
          const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
          return u.username || "-";
        },
      },
      {
        header: "Reviewed Date",
        accessorKey: "approved_date",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.approved_date} />
        ),
      },
      // {
      //   header: "Attachments",
      //   accessorKey: "pr_document",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }: { row: any }) => {
      //     return row.original.pr_document ? (
      //       <Link target="_blank" to={row.original.pr_document}>
      //         View Document
      //       </Link>
      //     ) : (
      //       "No Document"
      //     );
      //   },
      // },
           {
        header: "Notes",
        accessorKey: "notes",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const notes = row.original.notes;

          if (!notes) return "-";

          return (
            <Tooltip title={notes} arrow placement="top">
              <Box
                sx={{
                  maxWidth: 250,
                  display: "-webkit-box",
                  WebkitLineClamp: 4, // 👈 change to 3 if needed
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  cursor: "pointer",
                  lineHeight: "1.4em",
                  maxHeight: "5.6em", // 4 lines × lineHeight
                }}
              >
                {notes}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        header: "Tags",
        accessorKey: "tags",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const tags: string[] = row.original.tags || [];
          if (!tags.length) return "-";

          const maxVisible = 3;
          const visible = tags.slice(0, maxVisible);
          const remaining = tags.length - visible.length;
          const tooltipText = tags.join(", ");

          return (
            <Tooltip
              arrow
              placement="top"
              title={tooltipText}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "13px",
                    whiteSpace: "normal",
                  },
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                  minWidth: "150px",
                  cursor: "pointer",
                }}
              >
                {visible.map((tag: string) => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
                {remaining > 0 && <Chip>+{remaining} more</Chip>}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: false,
        enableSorting:  false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.created_by_user;
          return (
            <NameWithDate
              name={user?.username}
              date={row.original.created_at}
            />
          );
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.updated_by_user;
          return (
            <NameWithDate
              name={user?.username}
              date={row.original.updated_at}
            />
          );
        },
      },

      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const data = row.original;
          const menuItems: any[] = [];
          const canRevert = (row.original.status).toLowerCase() === "approved"
          
          // Always show "View Details"
          menuItems.push({
            label: "View Details",
            icon: VisibilityIcon,
            permission: canView,
            onClick: () =>
              navigate(
                `/tags-search/purchase-request-approved-pr-view-details/${data?.pr_id}`,
                {
                  state: { pr_id: data?.pr_id },
                }
              ),
            color: "primary",
          });

          // if (row.original.status?.toLowerCase() === "approved") {
          //   menuItems.push({
          //     label: "Create Purchase Order",
          //     icon: ShoppingBagIcon,
          //     permission: canCreate,
          //     onClick: () =>
          //       navigate(`/create-purchase-order/${data?.pr_id}`, {
          //         state: { pr_id: data?.pr_id },
          //       }),
          //     color: "primary",
          //   });
          // }
          if(canRevert){
            menuItems.push({
              icon: UndoIcon,
              label:
              revertingId === row.original.pr_id ? "Reverting..." : "Revert",
              permission: canUpdate,
              disabled: revertingId === row.original.pr_id,
              onClick: (pr: any) => {
                if (revertingId !== row.original.pr_id) handleRevert(pr?.pr_id);
              },
              color: "warning",
            });
          }
          return <DynamicMenuItem row={row.original} menuItems={menuItems} />;
        },
      },
    ],
    [dispatch, list, revertingId, canView, canCreate, canUpdate, navigate]
  );

  if (
    hideTagsSearchListingSection(
      noWrapper,
      loading,
      rowCount,
      (data as any[])?.length ?? 0,
      error,
    ) &&
    !forceShowOnTagsSearch
  ) {
    return null;
  }

  return (
    <Fragment>
      <Row className="align-items-center justify-content-between px-0 g-3 flex-wrap mb-3">


 
      </Row>

      {loading ? (
        <LoadingSpinner />
      ) : (
 
          <LoaderErrorWrapper loading={loading} error={error}>
            <MasterTableContainer
              columns={columns}
              data={data || []}
              totalRecords={rowCount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              onPageChange={setPageIndex}
              onPageSizeChange={setPageSize}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              colValue={setFilterValue}
              sorting={sorting}
              onSortingChange={setSorting}
              isBordered={false}
              isPagination={true}
              manualSorting={true}
              onTableReady={setTableInstance}
            />
          </LoaderErrorWrapper>
   
      )}
    </Fragment>
  );
};

export default ApprovedPR;
